import styled from '@emotion/styled';
import { tokens } from '@sunrun/experience-ui-components';
import {
  MEAL_BREAK_DURATION_MINUTES,
  SCHEDULER_AGREEMENT_URL,
  WORKDAY_URL,
} from 'constants/workday';

const TIME_CLOCK_HISTORY_DAY_RANGE = 4;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;
const MEAL_BREAK_DURATION_MS = MEAL_BREAK_DURATION_MINUTES * MS_PER_MINUTE;

const formatDateInTimeZone = (date: Date, timeZone: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

const getTimeClockHistoryDateRange = (
  timeZone: string,
  now = new Date(),
): { fromDate: string; toDate: string } => {
  const lookbackMs = (TIME_CLOCK_HISTORY_DAY_RANGE - 1) * MS_PER_DAY;
  return {
    fromDate: formatDateInTimeZone(
      new Date(now.getTime() - lookbackMs),
      timeZone,
    ),
    toDate: formatDateInTimeZone(now, timeZone),
  };
};

// Remaining minutes in a 30-minute meal break. Null when the break is over
// (elapsed >= 30 minutes) or the start time is invalid. Capped at 30.
const getMealBreakRemainingMinutes = (
  mealStartDateTime: string,
  now = new Date(),
): number | null => {
  const start = new Date(mealStartDateTime);
  if (Number.isNaN(start.getTime())) return null;

  const elapsedMs = now.getTime() - start.getTime();
  if (elapsedMs >= MEAL_BREAK_DURATION_MS) return null;

  const remainingMs = MEAL_BREAK_DURATION_MS - Math.max(0, elapsedMs);
  return Math.min(
    MEAL_BREAK_DURATION_MINUTES,
    Math.ceil(remainingMs / MS_PER_MINUTE),
  );
};

// Formats a Workday time clock event's datetime into the viewer's local time
// like "8:47 AM Aug 20, 2026".
const formatEventDateTime = (dateTime: string): string => {
  const date = new Date(dateTime);

  try {
    const time = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    const day = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
    return `${time} ${day}`;
  } catch {
    return date.toLocaleString('en-US');
  }
};

const WorkdayErrorCopy = ({
  onWorkdayClick,
}: {
  onWorkdayClick: () => void;
}) => (
  <>
    An error has occurred. Please refresh this page and try again. If you are
    unable to submit a clock in, clock out, or meal event, please go to{' '}
    <WorkdayLink
      href={WORKDAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onWorkdayClick}>
      Workday
    </WorkdayLink>
    .
  </>
);

const WorkdayLink = styled.a`
  color: ${tokens.BRAND_HERO_BLUE};
  text-decoration: underline;
`;

const SchedulerAgreementFooterNote = () => (
  <SchedulerAgreementNote data-testid="scheduler-agreement-footer-note">
    Schedulers are expected to not work more than 7 hours per day, 35 hours each
    week, and no more than 6 Non Productive Hours. Click{' '}
    <SchedulerAgreementLink
      href={SCHEDULER_AGREEMENT_URL}
      target="_blank"
      rel="noopener noreferrer">
      HERE
    </SchedulerAgreementLink>{' '}
    for the Scheduler Agreement.
  </SchedulerAgreementNote>
);

const SchedulerAgreementNote = styled.p`
  margin: 0;
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 12px;
  line-height: 1.4;
`;

const SchedulerAgreementLink = styled.a`
  color: ${tokens.TINTS_OFF_WHITE_20};
  text-decoration: underline;
`;

export {
  formatEventDateTime,
  getMealBreakRemainingMinutes,
  getTimeClockHistoryDateRange,
  SchedulerAgreementFooterNote,
  WorkdayErrorCopy,
  WorkdayLink,
};
